import { JSX } from "react";
import * as React from "react";

interface ToggleProps {
  /**
   *  Used for the QA automated test handle, and as the element's unique id
   */
  testId: string;
  /**
   *  The label to show above the range
   */
  label?: string | JSX.Element;
  /**
   *  The side of the toggle that the label will display on
   */
  labelSide?: "left" | "right";
  /**
   * True means that the component is toggled on, and false means that the component is toggled off
   */
  active?: boolean;
  /**
   * Whether the component should accept input or not
   */
  disabled?: boolean;
  /**
   * Selects the size of the toggle component
   */
  size?: "medium" | "small";
  /**
   * The class to assign to the outer container around the toggle and label text
   */
  containerClassName?: string;
  /**
   * Function that will run when the toggle is changed
   * @param isActive
   */
  onChange: (isActive: boolean) => void;
}

const ACTIVE_CLASS = "qes-toggle__active";
const INACTIVE_CLASS = "qes-toggle__inactive";
const DISABLED_CLASS = "qes-toggle__disabled";

const MEDIUM_CLASS = "qes-toggle__medium";
const SMALL_CLASS = "qes-toggle__small";

const RIGHT_CLASS = "qes-toggle__label-right";
const LEFT_CLASS = "qes-toggle__label-left";

export const Toggle: React.FC<ToggleProps> = ({
  testId,
  active = false,
  containerClassName,
  disabled = false,
  label,
  labelSide = "right",
  size = "small",
  onChange,
}) => {

  const size_class = size === "medium" ? MEDIUM_CLASS : SMALL_CLASS;
  const status_class = active ? ACTIVE_CLASS : INACTIVE_CLASS;
  const label_class = labelSide === "left" ? LEFT_CLASS : RIGHT_CLASS;

  // The label is a sibling of the switch rather than a wrapping <label>, so the two have to be tied together
  // explicitly or the switch reaches assistive tech unnamed. testId is required and unique, so it seeds the id.
  const labelId = `${testId}__label`;

  const handleClick = () => {
    if (disabled) {
      return;
    }

    onChange(active);
  }

  return (
    <div
      className={`qes-toggle__container ${containerClassName}`}
    >
      <div className={`${size_class} ${label_class} ${status_class} ${disabled ? DISABLED_CLASS : ""}`}>
        {!!label &&
          <div className={"qes-toggle__label"} id={labelId}>
            {label}
          </div>
        }
        <div
          id={testId}
          className="qes-toggle__base"
          tabIndex={0}
          role="switch"
          aria-checked={active}
          aria-disabled={disabled || undefined}
          aria-labelledby={!!label ? labelId : undefined}
          onClick={() => handleClick()}
          onKeyDown={(keyboardEvent) => {
            if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
              handleClick();
            }
          }}
        >
          <div
            className={`qes-toggle__circle`}
          />
        </div>
      </div>
    </div>
  );
}